import { SUBSCRIPTION_STATUS, SUBSCRIPTION_PLANS, PAYMENT_METHODS } from '../constants';

/**
 * Generar iniciales para avatares de suscripciones (basado en contacto)
 */
export const getSubscriptionInitials = (contactName) => {
  if (!contactName) return 'S';

  return contactName
    .split(' ')
    .map(word => word.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

/**
 * Formatear fecha para mostrar
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Formatear monto para mostrar
 */
export const formatAmount = (amount) => {
  if (!amount) return 'Sin monto';

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount);
};

/**
 * Obtener etiqueta de estado en español
 */
export const getStatusLabel = (status) => {
  const labels = {
    [SUBSCRIPTION_STATUS.ACTIVE]: 'Activa',
    [SUBSCRIPTION_STATUS.SUSPENDED]: 'Suspendida',
    [SUBSCRIPTION_STATUS.CANCELLED]: 'Cancelada',
  };

  return labels[status] || status;
};

/**
 * Obtener etiqueta de plan en español
 */
export const getPlanLabel = (plan) => {
  const labels = {
    [SUBSCRIPTION_PLANS.MONTHLY]: 'Mensual',
    [SUBSCRIPTION_PLANS.BIANNUAL]: 'Semestral',
    [SUBSCRIPTION_PLANS.ANNUAL]: 'Anual',
  };

  return labels[plan] || plan;
};

/**
 * Obtener etiqueta de método de pago en español
 */
export const getPaymentMethodLabel = (method) => {
  const labels = {
    [PAYMENT_METHODS.CREDIT_CARD]: 'Tarjeta de Crédito',
    [PAYMENT_METHODS.BANK_TRANSFER]: 'Transferencia',
    [PAYMENT_METHODS.CASH]: 'Efectivo',
  };

  return labels[method] || method;
};

/**
 * Verificar si una suscripción está vencida
 */
export const isExpired = (subscription) => {
  if (!subscription.fecha_fin) return false;

  const today = new Date();
  const endDate = new Date(subscription.fecha_fin);

  return endDate < today;
};

/**
 * Verificar si una suscripción vence pronto (7 días)
 */
export const isExpiringSoon = (subscription) => {
  if (!subscription.fecha_fin || subscription.estado !== SUBSCRIPTION_STATUS.ACTIVE) return false;

  const today = new Date();
  const endDate = new Date(subscription.fecha_fin);
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(today.getDate() + 7);

  return endDate >= today && endDate <= sevenDaysFromNow;
};

/**
 * Calcular días restantes de una suscripción
 */
export const getDaysRemaining = (subscription) => {
  if (!subscription.fecha_fin) return null;

  const today = new Date();
  const endDate = new Date(subscription.fecha_fin);
  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

/**
 * Procesar datos de suscripciones del backend
 */
export const processSubscriptionsData = (subscriptions) => {
  return subscriptions.map(subscription => ({
    ...subscription,
    statusLabel: getStatusLabel(subscription.estado),
    planLabel: getPlanLabel(subscription.plan),
    formattedAmount: formatAmount(subscription.monto),
    formattedStartDate: formatDate(subscription.fecha_inicio),
    formattedEndDate: formatDate(subscription.fecha_fin),
    isExpired: isExpired(subscription),
    isExpiringSoon: isExpiringSoon(subscription),
    daysRemaining: getDaysRemaining(subscription),
  }));
};

/**
 * Filtrar suscripciones por término de búsqueda y estado
 */
export const filterSubscriptions = (subscriptions, searchTerm, filterStatus = 'all') => {
  let filtered = subscriptions;

  // Filtro por estado
  if (filterStatus && filterStatus !== 'all') {
    filtered = filtered.filter(subscription => {
      if (filterStatus === 'activa') {
        return subscription.estado === 'activa' && !subscription.isExpired;
      } else if (filterStatus === 'vencida') {
        return subscription.estado === 'vencida' || subscription.isExpired;
      } else if (filterStatus === 'expiring_soon') {
        return isExpiringSoon(subscription);
      } else if (filterStatus === 'cancelada') {
        return subscription.estado === 'cancelada';
      }
      return true;
    });
  }

  // Filtro por término de búsqueda
  if (searchTerm && searchTerm.trim()) {
    const term = searchTerm.toLowerCase();

    filtered = filtered.filter(subscription =>
      subscription.contact_name?.toLowerCase().includes(term) ||
      subscription.contact_phone?.includes(term) ||
      subscription.contact_email?.toLowerCase().includes(term) ||
      subscription.statusLabel?.toLowerCase().includes(term) ||
      subscription.planLabel?.toLowerCase().includes(term) ||
      subscription.metodo_pago?.toLowerCase().includes(term)
    );
  }

  return filtered;
};

/**
 * Validar datos de suscripción
 */
export const validateSubscriptionData = (data) => {
  const errors = [];

  if (!data.contact_id?.trim()) {
    errors.push('El contacto es requerido');
  }

  if (!data.fecha_fin?.trim()) {
    errors.push('La fecha de fin es requerida');
  }

  if (data.fecha_inicio && data.fecha_fin) {
    const startDate = new Date(data.fecha_inicio);
    const endDate = new Date(data.fecha_fin);

    if (endDate <= startDate) {
      errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
    }
  }

  if (data.monto && (isNaN(data.monto) || data.monto < 0)) {
    errors.push('El monto debe ser un número válido mayor o igual a 0');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Preparar datos para envío al backend
 */
export const prepareSubscriptionForBackend = (formData) => {
  return {
    contact_id: formData.contact_id?.trim(),
    fecha_inicio: formData.fecha_inicio || new Date().toISOString().split('T')[0],
    fecha_fin: formData.fecha_fin?.trim(),
    estado: formData.estado || SUBSCRIPTION_STATUS.ACTIVE,
    plan: formData.plan || SUBSCRIPTION_PLANS.MONTHLY,
    metodo_pago: formData.metodo_pago?.trim() || null,
    monto: formData.monto ? parseFloat(formData.monto) : null,
    notas: formData.notas?.trim() || null,
  };
};

/**
 * Generar fecha de fin automática basada en el plan
 * DESHABILITADO - Ahora es manual para cortes de 15 días
 */
export const generateEndDate = (startDate, plan) => {
  // Retornar vacío para que sea manual
  return '';
}; 
